// hooks/useStyles.js
import { useState, useEffect, useCallback } from '@wordpress/element';

/**
 * Custom hook to fetch and manage styles data for Gutenberg blocks
 * 
 * This hook fetches the same styles data used by ShortcodeTable.jsx,
 * but formats it specifically for Gutenberg block SelectControl options.
 * 
 * @param {string} blockType - The type of block ('offer' or 'toplist')
 * @returns {Object} Styles data and related functions
 */
const useStyles = (blockType) => {
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch styles from the templates API
   */
  const fetchStyles = useCallback(async () => {
    setLoading(true);
    
    try {
      const response = await fetch('/wp-json/cas/v1/templates');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        // Extract styles for the specific block type
        const blockStyles = result.data[blockType] || {};
        
        // Convert to SelectControl options format
        const styleOptions = Object.entries(blockStyles).map(([styleName, styleInfo]) => ({
          label: `${styleName} (${styleInfo.source})`,
          value: styleName
        }));
        
        // Always include default option first
        const defaultOption = { label: 'Default', value: 'default' };
        const finalOptions = [defaultOption, ...styleOptions.filter(option => option.value !== 'default')];
        
        setStyles(finalOptions);
        setError(null);
      } else {
        // If no styles available, provide default option
        setStyles([{ label: 'Default', value: 'default' }]);
        setError(null);
      }
    } catch (e) {
      setError(e.message);
      // Fallback to default option on error
      setStyles([{ label: 'Default', value: 'default' }]);
      console.error('Error fetching styles:', e);
    } finally {
      setLoading(false);
    }
  }, [blockType]);

  // Fetch styles on mount and when blockType changes
  useEffect(() => {
    fetchStyles();
  }, [fetchStyles]);

  return {
    styles,
    loading,
    error,
    refetch: fetchStyles
  };
};

export default useStyles;
